// « Partenariats » (conception du 29/10/2026, § 7) : côté partenaire, les organisations qui l'ont invité, ce qu'elles
// lui partagent et lui transmettent, et la sortie d'un partenariat. Les droits réels sont ceux de la base
// (`leave_partnership` : le partenaire lui-même ou l'administrateur de son organisation).
import { ChevronRight } from "lucide-react";
import { useGetList, useNotify, useTranslate } from "ra-core";
import { useState } from "react";
import { Link } from "react-router";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Item,
  ItemActions,
  ItemContent,
  ItemGroup,
  ItemTitle,
} from "@/components/ui/item";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useIsMobile } from "@/hooks/use-mobile";

import { getSupabaseClient } from "../providers/supabase/supabase";
import type { Deal, PartnerOrg, Partnership, ProjectShare } from "../types";
import { type HostRow, hostRows, linksToLeave } from "./partnerships";
import { usePartnerDealHost, usePartnerDealTab } from "./PartnerDeals";
import { usePartnerOrg } from "./usePartnerOrg";

const all = { page: 1, perPage: 500 };

export const PartnershipsPage = () => {
  const translate = useTranslate();
  const { sale, partner, isPending } = usePartnerOrg();
  if (isPending) return null;
  if (!partner || !sale)
    return (
      <p className="mt-4 text-sm text-muted-foreground">
        {translate("resources.partnerships.hosts.not_partner")}
      </p>
    );
  return (
    <PartnershipsContent
      mySaleId={Number(sale.id)}
      myOrgId={sale.org_id ?? 0}
      orgAdmin={!!sale.org_admin}
    />
  );
};

const PartnershipsContent = ({
  mySaleId,
  myOrgId,
  orgAdmin,
}: {
  mySaleId: number;
  myOrgId: number;
  orgAdmin: boolean;
}) => {
  const translate = useTranslate();
  const notify = useNotify();
  const isMobile = useIsMobile();
  const [, setTab] = usePartnerDealTab();
  const [, setHost] = usePartnerDealHost();
  const [leaving, setLeaving] = useState<HostRow | null>(null);
  const [busy, setBusy] = useState(false);

  const { data: partnerships, refetch: refetchPartnerships } =
    useGetList<Partnership>("partnerships", {
      filter: { "host_org_id@neq": myOrgId, "ended_at@is": null },
      pagination: all,
      sort: { field: "id", order: "ASC" },
    });
  const { data: shares, refetch: refetchShares } = useGetList<ProjectShare>(
    "project_shares",
    {
      filter: { "host_org_id@neq": myOrgId, "revoked_at@is": null },
      pagination: all,
      sort: { field: "id", order: "ASC" },
    },
  );
  const { data: deals, refetch: refetchDeals } = useGetList<Deal>("deals", {
    filter: { "org_id@neq": myOrgId, "archived_at@is": null },
    pagination: all,
    sort: { field: "id", order: "ASC" },
  });
  const { data: orgs, refetch: refetchOrgs } = useGetList<PartnerOrg>(
    "partner_orgs",
    { pagination: all, sort: { field: "id", order: "ASC" } },
  );
  if (!partnerships || !shares || !deals || !orgs) return null;

  const rows = hostRows({ myOrgId, partnerships, orgs, shares, deals });

  const refresh = () => {
    refetchPartnerships();
    refetchShares();
    refetchDeals();
    refetchOrgs();
  };

  const name = (row: HostRow) => row.orgName ?? `#${row.hostOrgId}`;
  // Le lien appartient au partenaire ; l'administrateur de son organisation peut aussi le fermer (règle de la base).
  const canLeave = (row: HostRow) =>
    linksToLeave(row, mySaleId, orgAdmin).length > 0;

  const leave = async () => {
    if (!leaving) return;
    setBusy(true);
    try {
      // L'administrateur ferme les liens de tous ses membres vers cet hôte, un membre le sien.
      let error = null;
      for (const link of linksToLeave(leaving, mySaleId, orgAdmin)) {
        ({ error } = await getSupabaseClient().rpc("leave_partnership", {
          link,
        }));
        if (error) break;
      }
      refresh();
      if (error) {
        notify(error.message, { type: "error" });
        return;
      }
      notify("resources.partnerships.hosts.left", {
        type: "info",
        messageArgs: { name: name(leaving) },
      });
      setLeaving(null);
    } finally {
      setBusy(false);
    }
  };

  const date = (value: string) => new Date(value).toLocaleDateString();
  const sharesText = (row: HostRow) =>
    translate("resources.partnerships.hosts.shares", {
      smart_count: row.shares,
    });
  const dealsText = (row: HostRow) => {
    const text = translate("resources.partnerships.hosts.deals", {
      smart_count: row.deals,
    });
    return row.deals > 0 ? (
      <Link
        to="/deals"
        className="inline-flex min-h-11 items-center underline underline-offset-2 md:min-h-0"
        onClick={() => {
          setHost(row.hostOrgId);
          setTab("transmitted");
        }}
      >
        {text}
      </Link>
    ) : (
      text
    );
  };
  const leaveButton = (row: HostRow) =>
    canLeave(row) && (
      <Button
        size="sm"
        variant="outline"
        className="min-h-11 md:min-h-8"
        onClick={() => setLeaving(row)}
      >
        {translate("resources.partnerships.hosts.leave")}
      </Button>
    );

  return (
    <div className="flex flex-col gap-4 mt-4 max-w-4xl">
      <div className="flex flex-col gap-2">
        <h1 className="text-xl font-semibold">
          {translate("resources.partnerships.hosts.title")}
        </h1>
        <p className="text-sm text-muted-foreground">
          {translate("resources.partnerships.hosts.intro")}
        </p>
      </div>

      {rows.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          {translate("resources.partnerships.hosts.empty")}
        </p>
      ) : isMobile ? (
        <div className="flex flex-col gap-3">
          {rows.map((row) => (
            <Card key={row.hostOrgId}>
              <CardContent className="flex flex-col gap-2 text-sm">
                <span className="font-medium break-words">{name(row)}</span>
                <span className="text-muted-foreground">
                  {translate("resources.partnerships.hosts.since", {
                    date: date(row.since),
                  })}
                </span>
                <span>{sharesText(row)}</span>
                <span>{dealsText(row)}</span>
                {canLeave(row) && (
                  <div className="pt-1">{leaveButton(row)}</div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>
                {translate("resources.partnerships.mine.organisation")}
              </TableHead>
              <TableHead>
                {translate("resources.partnerships.mine.since")}
              </TableHead>
              <TableHead>
                {translate("resources.partnerships.mine.shares")}
              </TableHead>
              <TableHead>
                {translate("resources.partnerships.mine.deals")}
              </TableHead>
              <TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.hostOrgId}>
                <TableCell className="font-medium">{name(row)}</TableCell>
                <TableCell>{date(row.since)}</TableCell>
                <TableCell>{sharesText(row)}</TableCell>
                <TableCell>{dealsText(row)}</TableCell>
                <TableCell className="text-right">{leaveButton(row)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <Dialog
        open={!!leaving}
        onOpenChange={(open) => !open && setLeaving(null)}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {translate("resources.partnerships.hosts.leave_title", {
                name: leaving ? name(leaving) : "",
              })}
            </DialogTitle>
            <DialogDescription>
              {translate("resources.partnerships.hosts.leave_confirm", {
                name: leaving ? name(leaving) : "",
              })}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              disabled={busy}
              onClick={() => setLeaving(null)}
            >
              {translate("ra.action.cancel")}
            </Button>
            <Button variant="destructive" disabled={busy} onClick={leave}>
              {translate("resources.partnerships.hosts.leave")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

PartnershipsPage.path = "/partenariats";

/** Entrée de « Partenariats » dans les paramètres du téléphone, pour tout compte partenaire. */
export const PartnershipsSection = () => {
  const translate = useTranslate();
  const { partner } = usePartnerOrg();
  if (!partner) return null;
  return (
    <ItemGroup className="rounded-lg border overflow-hidden">
      <Item asChild size="sm" className="cursor-pointer">
        <Link to={PartnershipsPage.path}>
          <ItemContent>
            <ItemTitle className="font-normal">
              {translate("resources.partnerships.hosts.title")}
            </ItemTitle>
          </ItemContent>
          <ItemActions>
            <ChevronRight className="size-4 text-muted-foreground" />
          </ItemActions>
        </Link>
      </Item>
    </ItemGroup>
  );
};
